import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Info,
  CheckCircle,
  ArrowRight,
  User,
  Mail,
  Phone,
  FileText,
} from "lucide-react";

const fields = [
  { id: "name", label: "Name", type: "text", icon: <User size={18} /> },
  {
    id: "email",
    label: "Email Address",
    type: "email",
    icon: <Mail size={18} />,
  },
  {
    id: "phone",
    label: "Phone Number",
    type: "tel",
    icon: <Phone size={18} />,
  },
  {
    id: "message",
    label: "Message",
    type: "textarea",
    icon: <FileText size={18} />,
  },
];

const crmOptions = [
  { value: "name", label: "Full Name"  },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "notes", label: "Notes" },
  { value: "company", label: "Company" },
  { value: "ignore", label: "Do Not Sync"},
];

const FormMappingStep = () => {
  const theme = useTheme();

  const [mappings, setMappings] = useState({
    name: "name",
    email: "email",
    phone: "phone",
    message: "notes",
  });

  const handleMappingChange = (fieldId, value) => {
    setMappings((prev) => ({ ...prev, [fieldId]: value }));
  };

  const getMappedCount = () => {
    return Object.values(mappings).filter((v) => v !== "ignore").length;
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 4,
            color: theme.palette.getContrastText(theme.palette.primary.main),
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ArrowRight size={28} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Field Mapping
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Connect your form fields to CRM properties
              </Typography>
            </Box>
          </Stack>

          <Chip
            icon={<CheckCircle size={16} />}
            label={`${getMappedCount()} / ${fields.length} fields mapped`}
            sx={{
              bgcolor: "rgba(255,255,255,0.25)",
              color: "white",
              fontWeight: 600,
              backdropFilter: "blur(8px)",
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ p: 4 }}>
          <Stack spacing={3}>
            {fields.map((field, index) => {
              return (
                <Paper
                  key={field.id}
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.default",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow:
                        theme.palette.mode === "light"
                          ? "0 4px 16px rgba(0,0,0,0.08)"
                          : "0 4px 16px rgba(0,0,0,0.35)",
                    },
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={3}
                    alignItems={{ md: "center" }}
                  >
                    {/* Incoming field */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        textTransform="uppercase"
                        letterSpacing={0.5}
                        mb={1}
                        display="block"
                      >
                        Website Form Field
                      </Typography>

                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: theme.palette.primary.main + "20",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.main",
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Box>

                        <Box>
                          <Typography fontWeight={600} fontSize="0.95rem">
                            {field.label}
                          </Typography>
                          <Chip
                            label={field.type}
                            size="small"
                            sx={{
                              height: 20,
                              mt: 0.5,
                              fontSize: "0.7rem",
                              bgcolor: theme.palette.grey[200],
                              color: theme.palette.text.primary,
                            }}
                          />
                        </Box>
                      </Stack>
                    </Box>

                    {/* Arrow */}
                    <Box
                      sx={{
                        display: { xs: "none", md: "flex" },
                        color: "text.secondary",
                      }}
                    >
                      <ArrowRight size={24} />
                    </Box>

                    {/* CRM Field */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        textTransform="uppercase"
                        letterSpacing={0.5}
                        mb={1}
                        display="block"
                      >
                        CRM Field
                      </Typography>

                      <FormControl fullWidth>
                        <Select
                          size="small"
                          value={mappings[field.id]}
                          onChange={(e) =>
                            handleMappingChange(field.id, e.target.value)
                          }
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            bgcolor: "background.paper",
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                            },
                          }}
                        >
                          {crmOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                {option.icon}
                                {option.label}
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          <Divider sx={{ my: 4 }} />

          {/* Info footer */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: theme.palette.info.light,
              bgcolor: theme.palette.info.light + "22",
            }}
          >
            <Stack direction="row" spacing={2}>
              <Info size={20} style={{ color: theme.palette.info.main }} />
              <Box>
                <Typography fontWeight={600} color="info.dark" mb={0.5}>
                  How Field Mapping Works
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When a visitor submits your form, these mapped fields
                  determine how the data is synced into your CRM. Fields set to
                  "Do Not Sync" will be ignored.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default FormMappingStep;
